import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
    User,
    ArrowLeft,
    Building,
    Mail,
    XCircle,
    CheckCircle,
    AlertCircle,
    Loader2,
    Search,
    ChevronDown,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { adminService, AdminUpdateUserData, AdminUserDetailResponse } from '@/services/adminService'
import { helpersService, CryptoType } from '@/services/helpersService'
import { adminMerchantService } from '@/services/adminMerchantService'
import { Country, Currency } from '@/types'

import { useAuth } from '@/hooks/useAuth'
import { getVisibleRoles } from '@/utils/auth'
import { useAuditLog } from '@/utils/auditLogger'

interface MerchantOption {
    id: number | string
    company_name: string
    email: string
    status: string
}

export default function AdminEditUser() {
    const { branding } = useBranding()
    const pageStyles = getPageStyles(branding)
    const cardStyles = getCardStyles(branding)
    const statusColors = getStatusColors(branding)
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const { user } = useAuth()
    const { logDataModification } = useAuditLog()
    const [loading, setLoading] = useState(false)
    const [fetchingUser, setFetchingUser] = useState(true)
    const [countries, setCountries] = useState<Country[]>([])
    const [currencies, setCurrencies] = useState<Currency[]>([])
    const [cryptoTypes, setCryptoTypes] = useState<CryptoType[]>([])
    const [merchants, setMerchants] = useState<MerchantOption[]>([])
    const [countrySearchQuery, setCountrySearchQuery] = useState('')
    const [currencySearchQuery, setCurrencySearchQuery] = useState('')
    const [cryptoTypeSearchQuery, setCryptoTypeSearchQuery] = useState('')
    const [merchantSearchQuery, setMerchantSearchQuery] = useState('')
    const [showCountryDropdown, setShowCountryDropdown] = useState(false)
    const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false)
    const [showCryptoTypeDropdown, setShowCryptoTypeDropdown] = useState(false)
    const [showMerchantDropdown, setShowMerchantDropdown] = useState(false)
    const [countryLoading, setCountryLoading] = useState(false)
    const [currencyLoading, setCurrencyLoading] = useState(false)
    const [cryptoTypeLoading, setCryptoTypeLoading] = useState(false)
    const [merchantLoading, setMerchantLoading] = useState(false)
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [userData, setUserData] = useState<AdminUserDetailResponse['user'] | null>(null)
    const [updateSuccess, setUpdateSuccess] = useState(false)

    const countryDropdownRef = useRef<HTMLDivElement>(null)
    const currencyDropdownRef = useRef<HTMLDivElement>(null)
    const cryptoTypeDropdownRef = useRef<HTMLDivElement>(null)
    const merchantDropdownRef = useRef<HTMLDivElement>(null)

    const [formData, setFormData] = useState<AdminUpdateUserData & {
        create_merchant?: boolean
        merchant_id?: number
        merchant_name?: string
        merchant_email?: string
        merchant_description?: string
        merchant_website_url?: string
        merchant_currency_id?: number
        merchant_country_id?: number
        merchant_success_redirect_url?: string
        merchant_cancel_redirect_url?: string
        merchant_webhook_url?: string
        merchant_commission_rate?: number
        merchant_payout_crypto_enabled?: boolean
        merchant_payout_wallet_address?: string
        merchant_payout_crypto_type_id?: number
        send_welcome_email?: boolean
    }>({
        first_name: '',
        last_name: '',
        role: '',
        company_name: '',
        phone_number: '',
        country: undefined,
        is_active: true,
        is_verified: false,
        create_merchant: false,
        merchant_id: undefined,
        merchant_name: '',
        merchant_email: '',
        merchant_description: '',
        merchant_website_url: '',
        merchant_currency_id: undefined,
        merchant_country_id: undefined,
        merchant_success_redirect_url: '',
        merchant_cancel_redirect_url: '',
        merchant_webhook_url: '',
        merchant_commission_rate: 0.00,
        merchant_payout_crypto_enabled: false,
        merchant_payout_wallet_address: '',
        merchant_payout_crypto_type_id: undefined,
        send_welcome_email: false,
    })

    useEffect(() => {
        if (id) {
            fetchUser()
            fetchCountries()
            fetchCurrencies()
            fetchCryptoTypes()
            fetchMerchants()
        }
    }, [id])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                countryDropdownRef.current &&
                !countryDropdownRef.current.contains(event.target as Node)
            ) {
                setShowCountryDropdown(false)
            }
            if (
                currencyDropdownRef.current &&
                !currencyDropdownRef.current.contains(event.target as Node)
            ) {
                setShowCurrencyDropdown(false)
            }
            if (
                cryptoTypeDropdownRef.current &&
                !cryptoTypeDropdownRef.current.contains(event.target as Node)
            ) {
                setShowCryptoTypeDropdown(false)
            }
            if (
                merchantDropdownRef.current &&
                !merchantDropdownRef.current.contains(event.target as Node)
            ) {
                setShowMerchantDropdown(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const fetchUser = async () => {
        if (!id) return

        setFetchingUser(true)
        try {
            const response = await adminService.getUserById(parseInt(id))

            if (response && response.success && response.user) {
                setUserData(response.user)
                const userMerchant = response.user.merchant
                setFormData({
                    first_name: response.user.first_name || '',
                    last_name: response.user.last_name || '',
                    role: response.user.role || '',
                    company_name: response.user.company_name || '',
                    phone_number: response.user.phone_number || '',
                    country: response.user.country?.id,
                    is_active: response.user.is_active,
                    is_verified: response.user.is_verified,
                    create_merchant: false,
                    merchant_id: userMerchant?.id,
                    merchant_name: userMerchant?.company_name || '',
                    merchant_email: userMerchant?.email || '',
                    merchant_description: userMerchant?.description || '',
                    merchant_website_url: userMerchant?.website_url || '',
                    merchant_currency_id: undefined, // Will need to fetch currency ID from currency code
                    merchant_country_id: undefined, // Will need to fetch country ID
                    merchant_success_redirect_url: userMerchant?.success_redirect_url || '',
                    merchant_cancel_redirect_url: userMerchant?.cancel_redirect_url || '',
                    merchant_webhook_url: userMerchant?.webhook_url || '',
                    merchant_commission_rate: userMerchant?.commission_rate || 0.00,
                    merchant_payout_crypto_enabled: userMerchant?.payout_crypto_enabled || false,
                    merchant_payout_wallet_address: userMerchant?.payout_wallet_address || '',
                    merchant_payout_crypto_type_id: undefined, // Will be set after fetching crypto types
                    send_welcome_email: false,
                })
                if (response.user.country) {
                    setCountrySearchQuery(
                        `${response.user.country.country_code} - ${response.user.country.name}`
                    )
                }
                if (userMerchant) {
                    setMerchantSearchQuery(`${userMerchant.company_name} - ${userMerchant.email}`)

                    // Update formData with merchant information
                    setFormData(prev => ({
                        ...prev,
                        merchant_id: userMerchant.id,
                        merchant_name: userMerchant.company_name || '',
                        merchant_email: userMerchant.email || '',
                        merchant_description: userMerchant.description || '',
                        merchant_website_url: userMerchant.website_url || '',
                        merchant_success_redirect_url: userMerchant.success_redirect_url || '',
                        merchant_cancel_redirect_url: userMerchant.cancel_redirect_url || '',
                        merchant_webhook_url: userMerchant.webhook_url || '',
                        merchant_commission_rate: userMerchant.commission_rate || 0.00,
                        merchant_payout_crypto_enabled: userMerchant.payout_crypto_enabled || false,
                        merchant_payout_wallet_address: userMerchant.payout_wallet_address || '',
                    }))

                    // Set crypto type if merchant has one - will be set after crypto types are fetched
                    if (userMerchant.payout_crypto_type) {
                        // Store crypto type name for later matching
                        const cryptoTypeName = userMerchant.payout_crypto_type
                        fetchCryptoTypes().then((cryptoTypesData) => {
                            const cryptoType = cryptoTypesData.find(ct => ct.name === cryptoTypeName)
                            if (cryptoType) {
                                setFormData(prev => ({
                                    ...prev,
                                    merchant_payout_crypto_type_id: cryptoType.id,
                                }))
                                setCryptoTypeSearchQuery(`${cryptoType.code} - ${cryptoType.name}`)
                            }
                        })
                    }

                    // Set currency if merchant has one - will be set after currencies are fetched
                    if (userMerchant.currency) {
                        const currencyCode = userMerchant.currency
                        fetchCurrencies().then((currenciesData) => {
                            const currency = currenciesData.find(c => c.code === currencyCode)
                            if (currency) {
                                setFormData(prev => ({
                                    ...prev,
                                    merchant_currency_id: currency.id,
                                }))
                                setCurrencySearchQuery(`${currency.code} - ${currency.name}`)
                            }
                        })
                    }
                } else {
                    // No merchant exists, allow creating new one
                    setFormData(prev => ({
                        ...prev,
                        create_merchant: false, // Default to false, user can toggle
                    }))
                }
            } else {
                setErrors({ general: response?.message || 'Failed to fetch user' })
            }
        } catch (err: any) {
            setErrors({ general: adminService.handleError(err) })
        } finally {
            setFetchingUser(false)
        }
    }

    const fetchCountries = async () => {
        try {
            const data = await helpersService.getCountries()
            setCountries(data)
        } catch (error) {
            console.error('Failed to fetch countries:', error)
        }
    }

    const fetchCurrencies = async () => {
        try {
            const data = await helpersService.getCurrencies()
            setCurrencies(data)
            return data
        } catch (error) {
            console.error('Failed to fetch currencies:', error)
            return []
        }
    }

    const fetchCryptoTypes = async () => {
        try {
            const data = await helpersService.getActiveCryptoTypes()
            setCryptoTypes(data)
            return data
        } catch (error) {
            console.error('Failed to fetch crypto types:', error)
            return []
        }
    }


    const fetchMerchants = async () => {
        try {
            const response = await adminMerchantService.getAllMerchants({ page_size: 100 })
            if (response.results) {
                setMerchants(response.results.map(m => ({
                    id: typeof m.id === 'string' ? parseInt(m.id) : m.id,
                    company_name: m.company_name,
                    email: m.email,
                    status: m.status
                })))
            }
        } catch (error) {
            console.error('Failed to fetch merchants:', error)
        }
    }

    const searchCountries = async (query: string) => {
        if (!query.trim()) {
            fetchCountries()
            return
        }

        setCountryLoading(true)
        try {
            const data = await helpersService.searchCountries(query)
            setCountries(data)
        } catch (error) {
            console.error('Failed to search countries:', error)
        } finally {
            setCountryLoading(false)
        }
    }

    const handleCountrySearch = (query: string) => {
        setCountrySearchQuery(query)
        if (query.length >= 2) {
            searchCountries(query)
        } else if (query.length === 0) {
            fetchCountries()
        }
    }

    const searchCurrencies = async (query: string) => {
        if (!query.trim()) {
            fetchCurrencies()
            return
        }

        setCurrencyLoading(true)
        try {
            const data = await helpersService.searchCurrencies(query)
            setCurrencies(data)
        } catch (error) {
            console.error('Failed to search currencies:', error)
        } finally {
            setCurrencyLoading(false)
        }
    }

    const searchMerchants = async (query: string) => {
        if (!query.trim()) {
            fetchMerchants()
            return
        }

        setMerchantLoading(true)
        try {
            const response = await adminMerchantService.getAllMerchants({
                search: query,
                page_size: 50
            })
            if (response.results) {
                setMerchants(response.results.map(m => ({
                    id: typeof m.id === 'string' ? parseInt(m.id) : m.id,
                    company_name: m.company_name,
                    email: m.email,
                    status: m.status
                })))
            }
        } catch (error) {
            console.error('Failed to search merchants:', error)
        } finally {
            setMerchantLoading(false)
        }
    }

    const handleCurrencySearch = (query: string) => {
        setCurrencySearchQuery(query)
        if (query.length >= 2) {
            searchCurrencies(query)
        } else if (query.length === 0) {
            fetchCurrencies()
        }
    }

    const searchCryptoTypes = async (query: string) => {
        if (!query.trim()) {
            fetchCryptoTypes()
            return
        }

        setCryptoTypeLoading(true)
        try {
            const data = await helpersService.searchCryptoTypes(query)
            setCryptoTypes(data)
        } catch (error) {
            console.error('Failed to search crypto types:', error)
        } finally {
            setCryptoTypeLoading(false)
        }
    }

    const handleCryptoTypeSearch = (query: string) => {
        setCryptoTypeSearchQuery(query)
        if (query.length >= 2) {
            searchCryptoTypes(query)
        } else if (query.length === 0) {
            fetchCryptoTypes()
        }
    }

    const handleMerchantSearch = (query: string) => {
        setMerchantSearchQuery(query)
        if (query.length >= 2) {
            searchMerchants(query)
        } else if (query.length === 0) {
            fetchMerchants()
        }
    }

    const selectCountry = (country: Country) => {
        setFormData(prev => ({
            ...prev,
            country: country.id,
            merchant_country_id: country.id,
        }))
        setCountrySearchQuery(`${country.country_code} - ${country.name}`)
        setShowCountryDropdown(false)
    }

    const selectCurrency = (currency: Currency) => {
        setFormData(prev => ({
            ...prev,
            merchant_currency_id: currency.id,
        }))
        setCurrencySearchQuery(`${currency.code} - ${currency.name}`)
        setShowCurrencyDropdown(false)
    }

    const selectCryptoType = (cryptoType: CryptoType) => {
        setFormData(prev => ({
            ...prev,
            merchant_payout_crypto_type_id: cryptoType.id,
        }))
        setCryptoTypeSearchQuery(`${cryptoType.code} - ${cryptoType.name}`)
        setShowCryptoTypeDropdown(false)
    }

    const selectMerchant = (merchant: MerchantOption) => {
        setFormData(prev => ({
            ...prev,
            merchant_id: typeof merchant.id === 'string' ? parseInt(merchant.id) : merchant.id,
            create_merchant: false,
        }))
        setMerchantSearchQuery(`${merchant.company_name} - ${merchant.email}`)
        setShowMerchantDropdown(false)
    }

    const handleInputChange = (
        field: keyof typeof formData,
        value: string | number | boolean | undefined
    ) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }))
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev }
                delete newErrors[field]
                return newErrors
            })
        }
    }

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {}

        if (!formData.first_name?.trim()) {
            newErrors.first_name = 'First name is required'
        }

        if (!formData.last_name?.trim()) {
            newErrors.last_name = 'Last name is required'
        }

        if (!formData.role) {
            newErrors.role = 'Role is required'
        }

        // Validate merchant creation if enabled
        if (formData.create_merchant) {
            if (!formData.merchant_name?.trim()) {
                newErrors.merchant_name = 'Merchant name is required when creating merchant'
            }
            if (!formData.merchant_email?.trim()) {
                newErrors.merchant_email = 'Merchant email is required when creating merchant'
            }
            if (!formData.merchant_currency_id) {
                newErrors.merchant_currency_id = 'Merchant currency is required when creating merchant'
            }

            // Validate payout configuration
            if (formData.merchant_payout_crypto_enabled) {
                if (!formData.merchant_payout_wallet_address?.trim()) {
                    newErrors.merchant_payout_wallet_address = 'Payout wallet address is required when crypto payouts are enabled'
                }
                if (!formData.merchant_payout_crypto_type_id) {
                    newErrors.merchant_payout_crypto_type_id = 'Payout crypto type is required when crypto payouts are enabled'
                }
            }
        } else {
            // Validate merchant selection for merchant roles
            if (formData.role?.includes('merchant') && !formData.merchant_id) {
                newErrors.merchant_id = 'Merchant ID is required for merchant roles'
            }
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm() || !id) {
            return
        }

        setLoading(true)
        setErrors({})
        try {
            // Prepare payload - remove merchant_id if creating new merchant
            const payload = { ...formData }
            if (payload.create_merchant) {
                delete payload.merchant_id
            }

            const response = await adminService.updateUser(parseInt(id), payload)

            if (response.success) {
                // Log successful user update
                logDataModification(
                    user,
                    'update_user',
                    'user',
                    parseInt(id),
                    true,
                    'User updated successfully',
                    {
                        userId: parseInt(id),
                        userEmail: userData?.email,
                        userRole: formData.role,
                    }
                )

                setUpdateSuccess(true)
                setTimeout(() => {
                    navigate(`/admin/users/${id}`)
                }, 2000)
            } else {
                // Log failed user update
                logDataModification(
                    user,
                    'update_user',
                    'user',
                    parseInt(id),
                    false,
                    'Failed to update user',
                    {
                        userId: parseInt(id),
                        error: response.message,
                    }
                )
                setErrors({ general: response.message || 'Failed to update user' })
            }
        } catch (error: any) {
            const errorMessage = adminService.handleError(error)
            setErrors({ general: errorMessage })
        } finally {
            setLoading(false)
        }
    }

    if (fetchingUser) {
        return (
            <div
                className="min-h-screen flex items-center justify-center"
                style={pageStyles.container}
            >
                <div className="text-center">
                    <Loader2
                        className="w-8 h-8 animate-spin mx-auto mb-4"
                        style={{ color: branding.primaryColor }}
                    />
                    <p style={pageStyles.textSecondary}>Loading user data...</p>
                </div>
            </div>
        )
    }

    if (updateSuccess) {
        return (
            <div
                className="min-h-screen"
                style={pageStyles.container}
            >
                <div className="relative z-10 p-6">
                    <div className="mb-8">
                        <div className="flex items-center mb-6 space-x-4">
                            <Button
                                variant="ghost"
                                onClick={() => navigate('/admin/users')}
                                className="p-2 text-white rounded-lg hover:bg-gray-800"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </Button>
                            <div>
                                <div className="flex items-center space-x-3 mb-2">
                                    <div
                                        className="p-2 rounded-lg"
                                        style={{
                                            backgroundColor: statusColors.success.backgroundColor
                                        }}
                                    >
                                        <CheckCircle
                                            className="w-6 h-6"
                                            style={{ color: statusColors.success.color }}
                                        />
                                    </div>
                                    <h1
                                        className="text-4xl font-bold"
                                        style={pageStyles.text}
                                    >
                                        User Updated Successfully
                                    </h1>
                                </div>
                                <p style={pageStyles.textSecondary}>
                                    Redirecting to user details...
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (errors.general && !userData) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-white mb-2">Error</h2>
                    <p className="text-gray-300 mb-4">{errors.general}</p>
                    <Button
                        onClick={() => navigate('/admin/users')}
                        className="px-6 py-3 text-white bg-orange-500 hover:bg-orange-600"
                    >
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Back to Users
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div
            className="min-h-screen"
            style={pageStyles.container}
        >
            <div className="relative z-10 p-6">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center mb-6 space-x-4">
                        <Button
                            variant="ghost"
                            onClick={() => navigate(`/admin/users/${id}`)}
                            className="p-2 text-white rounded-lg hover:bg-gray-800"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <div>
                            <div className="flex items-center space-x-3 mb-2">
                                <div
                                    className="p-2 rounded-lg"
                                    style={{
                                        backgroundColor: `${branding.primaryColor}20`
                                    }}
                                >
                                    <User
                                        className="w-6 h-6"
                                        style={{ color: branding.primaryColor }}
                                    />
                                </div>
                                <h1
                                    className="text-4xl font-bold"
                                    style={pageStyles.text}
                                >
                                    Edit User
                                </h1>
                            </div>
                            <p style={pageStyles.textSecondary}>
                                Update user information
                            </p>
                        </div>
                    </div>
                </div>

                {/* Form Card */}
                <div
                    className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
                    style={cardStyles}
                >
                    <div className="p-8 card-content">
                        <form onSubmit={handleSubmit} className="space-y-8">
                            {/* General Error */}
                            {errors.general && (
                                <div
                                    className="flex items-center p-4 space-x-3 border rounded-lg"
                                    style={{
                                        backgroundColor: statusColors.error.backgroundColor,
                                        borderColor: statusColors.error.borderColor
                                    }}
                                >
                                    <AlertCircle
                                        className="flex-shrink-0 w-5 h-5"
                                        style={{ color: statusColors.error.color }}
                                    />
                                    <p style={{ color: statusColors.error.color }}>{errors.general}</p>
                                </div>
                            )}

                            {/* User Information */}
                            <div className="space-y-6">
                                <div className="pb-4">
                                    <h2
                                        className="flex items-center space-x-3 text-2xl font-semibold"
                                        style={pageStyles.text}
                                    >
                                        <div
                                            className="p-2 rounded-lg"
                                            style={{
                                                backgroundColor: `${branding.primaryColor}20`
                                            }}
                                        >
                                            <User
                                                className="w-6 h-6"
                                                style={{ color: branding.primaryColor }}
                                            />
                                        </div>
                                        <span>User Information</span>
                                    </h2>
                                    <p
                                        className="mt-2"
                                        style={pageStyles.textSecondary}
                                    >
                                        Update the user's basic information
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                    <div>
                                        <label
                                            className="block mb-3 text-sm font-semibold"
                                            style={pageStyles.textSecondary}
                                        >
                                            First Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.first_name || ''}
                                            onChange={e => handleInputChange('first_name', e.target.value)}
                                            className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 ${errors.first_name
                                                ? 'border-orange-500 focus:ring-orange-500'
                                                : 'hover:border-gray-500'
                                                }`}
                                            style={{
                                                backgroundColor: `${branding.surfaceColor}80`,
                                                borderColor: errors.first_name
                                                    ? statusColors.error.borderColor
                                                    : `${branding.textSecondaryColor}30`,
                                                color: branding.textColor,
                                            }}
                                            placeholder="John"
                                        />
                                        {errors.first_name && (
                                            <p
                                                className="flex items-center mt-2 space-x-1 text-sm"
                                                style={{ color: statusColors.error.color }}
                                            >
                                                <XCircle className="w-4 h-4" />
                                                <span>{errors.first_name}</span>
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label
                                            className="block mb-3 text-sm font-semibold"
                                            style={pageStyles.textSecondary}
                                        >
                                            Last Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.last_name || ''}
                                            onChange={e => handleInputChange('last_name', e.target.value)}
                                            className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 ${errors.last_name
                                                ? 'border-orange-500 focus:ring-orange-500'
                                                : 'hover:border-gray-500'
                                                }`}
                                            style={{
                                                backgroundColor: `${branding.surfaceColor}80`,
                                                borderColor: errors.last_name
                                                    ? statusColors.error.borderColor
                                                    : `${branding.textSecondaryColor}30`,
                                                color: branding.textColor,
                                            }}
                                            placeholder="Doe"
                                        />
                                        {errors.last_name && (
                                            <p
                                                className="flex items-center mt-2 space-x-1 text-sm"
                                                style={{ color: statusColors.error.color }}
                                            >
                                                <XCircle className="w-4 h-4" />
                                                <span>{errors.last_name}</span>
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label
                                            className="block mb-3 text-sm font-semibold"
                                            style={pageStyles.textSecondary}
                                        >
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            value={userData?.email || ''}
                                            disabled
                                            className="w-full px-4 py-3 border rounded-xl opacity-60 cursor-not-allowed"
                                            style={{
                                                backgroundColor: `${branding.surfaceColor}40`,
                                                borderColor: `${branding.textSecondaryColor}20`,
                                                color: branding.textColor,
                                            }}
                                            placeholder="Email cannot be changed"
                                        />
                                        <p
                                            className="mt-2 text-xs"
                                            style={pageStyles.textSecondary}
                                        >
                                            Email cannot be modified
                                        </p>
                                    </div>

                                    <div>
                                        <label
                                            className="block mb-3 text-sm font-semibold"
                                            style={pageStyles.textSecondary}
                                        >
                                            Role *
                                        </label>
                                        <select
                                            value={formData.role || ''}
                                            onChange={e => handleInputChange('role', e.target.value)}
                                            className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 ${errors.role
                                                ? 'border-orange-500 focus:ring-orange-500'
                                                : 'hover:border-gray-500'
                                                }`}
                                            style={{
                                                backgroundColor: `${branding.surfaceColor}80`,
                                                borderColor: errors.role
                                                    ? statusColors.error.borderColor
                                                    : `${branding.textSecondaryColor}30`,
                                                color: branding.textColor,
                                            }}
                                        >
                                            <option value="">Select a role</option>
                                            {getVisibleRoles(user).map(role => (
                                                <option key={role} value={role}>
                                                    {role === 'admin'
                                                        ? 'Platform Admin'
                                                        : role === 'merchant_admin'
                                                            ? 'Merchant Admin'
                                                            : role === 'merchant_user'
                                                                ? 'Merchant User'
                                                                : role === 'submerchant_admin'
                                                                    ? 'Submerchant Admin'
                                                                    : role === 'submerchant_user'
                                                                        ? 'Submerchant User'
                                                                        : role === 'auditor'
                                                                            ? 'Auditor'
                                                                            : role}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.role && (
                                            <p
                                                className="flex items-center mt-2 space-x-1 text-sm"
                                                style={{ color: statusColors.error.color }}
                                            >
                                                <XCircle className="w-4 h-4" />
                                                <span>{errors.role}</span>
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label
                                            className="block mb-3 text-sm font-semibold"
                                            style={pageStyles.textSecondary}
                                        >
                                            Company Name
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.company_name || ''}
                                            onChange={e => handleInputChange('company_name', e.target.value)}
                                            className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                            style={{
                                                backgroundColor: `${branding.surfaceColor}80`,
                                                borderColor: `${branding.textSecondaryColor}30`,
                                                color: branding.textColor,
                                            }}
                                            placeholder="Company Name"
                                        />
                                    </div>

                                    <div>
                                        <label
                                            className="block mb-3 text-sm font-semibold"
                                            style={pageStyles.textSecondary}
                                        >
                                            Phone Number
                                        </label>
                                        <input
                                            type="tel"
                                            value={formData.phone_number || ''}
                                            onChange={e => handleInputChange('phone_number', e.target.value)}
                                            className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                            style={{
                                                backgroundColor: `${branding.surfaceColor}80`,
                                                borderColor: `${branding.textSecondaryColor}30`,
                                                color: branding.textColor,
                                            }}
                                            placeholder="+1234567890"
                                        />
                                    </div>

                                    <div>
                                        <label
                                            className="block mb-3 text-sm font-semibold"
                                            style={pageStyles.textSecondary}
                                        >
                                            Country
                                        </label>
                                        <div className="relative" ref={countryDropdownRef}>
                                            <div className="relative">
                                                <Search
                                                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                                                    style={{ color: branding.textSecondaryColor }}
                                                />
                                                <input
                                                    type="text"
                                                    value={countrySearchQuery}
                                                    onChange={e => handleCountrySearch(e.target.value)}
                                                    onFocus={() => setShowCountryDropdown(true)}
                                                    className="w-full px-4 py-3 pl-10 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                                    style={{
                                                        backgroundColor: `${branding.surfaceColor}80`,
                                                        borderColor: `${branding.textSecondaryColor}30`,
                                                        color: branding.textColor,
                                                    }}
                                                    placeholder="Search country..."
                                                />
                                            </div>
                                            {showCountryDropdown && (
                                                <div
                                                    className="absolute z-10 w-full mt-2 overflow-y-auto border rounded-xl shadow-lg max-h-60"
                                                    style={{
                                                        backgroundColor: branding.surfaceColor,
                                                        borderColor: `${branding.textSecondaryColor}30`,
                                                    }}
                                                >
                                                    {countryLoading ? (
                                                        <div className="flex items-center justify-center p-4">
                                                            <Loader2 className="w-5 h-5 animate-spin" style={{ color: branding.primaryColor }} />
                                                        </div>
                                                    ) : countries.length > 0 ? (
                                                        countries.map(country => (
                                                            <button
                                                                key={country.id}
                                                                type="button"
                                                                onClick={() => selectCountry(country)}
                                                                className="w-full px-4 py-3 text-left hover:bg-gray-700 transition-colors"
                                                                style={{
                                                                    color: branding.textColor,
                                                                }}
                                                            >
                                                                {country.country_code} - {country.name}
                                                            </button>
                                                        ))
                                                    ) : (
                                                        <div className="p-4 text-center" style={{ color: branding.textSecondaryColor }}>
                                                            No countries found
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Status Section */}
                            <div className="space-y-6">
                                <div className="pb-4">
                                    <h2
                                        className="flex items-center space-x-3 text-2xl font-semibold"
                                        style={pageStyles.text}
                                    >
                                        <div
                                            className="p-2 rounded-lg"
                                            style={{
                                                backgroundColor: `${branding.primaryColor}20`
                                            }}
                                        >
                                            <Building
                                                className="w-6 h-6"
                                                style={{ color: branding.primaryColor }}
                                            />
                                        </div>
                                        <span>Status</span>
                                    </h2>
                                    <p
                                        className="mt-2"
                                        style={pageStyles.textSecondary}
                                    >
                                        Manage user account status
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                    <div className="flex items-center space-x-3">
                                        <input
                                            type="checkbox"
                                            id="is_active"
                                            checked={formData.is_active || false}
                                            onChange={e => handleInputChange('is_active', e.target.checked)}
                                            className="w-5 h-5 rounded border-gray-600 focus:ring-2 focus:ring-orange-500"
                                            style={{
                                                accentColor: branding.primaryColor,
                                            }}
                                        />
                                        <label
                                            htmlFor="is_active"
                                            className="text-sm font-medium"
                                            style={pageStyles.text}
                                        >
                                            Active
                                        </label>
                                    </div>

                                    <div className="flex items-center space-x-3">
                                        <input
                                            type="checkbox"
                                            id="is_verified"
                                            checked={formData.is_verified || false}
                                            onChange={e => handleInputChange('is_verified', e.target.checked)}
                                            className="w-5 h-5 rounded border-gray-600 focus:ring-2 focus:ring-orange-500"
                                            style={{
                                                accentColor: branding.primaryColor,
                                            }}
                                        />
                                        <label
                                            htmlFor="is_verified"
                                            className="text-sm font-medium"
                                            style={pageStyles.text}
                                        >
                                            Verified
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Merchant Creation Section - Only show for merchant roles */}
                            {(formData.role?.includes('merchant') || formData.role?.includes('submerchant')) && (
                                <div className="space-y-6">
                                    <div className="pb-4 border-b" style={{ borderBottomColor: `${branding.textSecondaryColor}20` }}>
                                        <h2
                                            className="flex items-center space-x-3 text-2xl font-semibold"
                                            style={pageStyles.text}
                                        >
                                            <div
                                                className="p-2 rounded-lg"
                                                style={{
                                                    backgroundColor: `${branding.primaryColor}20`
                                                }}
                                            >
                                                <Building
                                                    className="w-6 h-6"
                                                    style={{ color: branding.primaryColor }}
                                                />
                                            </div>
                                            <span>Merchant Creation</span>
                                        </h2>
                                        <p
                                            className="mt-2"
                                            style={pageStyles.textSecondary}
                                        >
                                            Choose whether to create a new merchant or assign to existing one
                                        </p>
                                    </div>

                                    <div className="flex items-center space-x-4">
                                        <label className="flex items-center space-x-3 cursor-pointer group">
                                            <div className="relative">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.create_merchant || false}
                                                    onChange={e => handleInputChange('create_merchant', e.target.checked)}
                                                    className="sr-only"
                                                />
                                                <div
                                                    className={`w-5 h-5 border-2 rounded-md transition-all duration-200 flex items-center justify-center ${formData.create_merchant
                                                        ? 'shadow-lg'
                                                        : 'group-hover:border-orange-400 group-hover:bg-gray-600/50'
                                                        }`}
                                                    style={{
                                                        backgroundColor: formData.create_merchant ? branding.primaryColor : `${branding.surfaceColor}80`,
                                                        borderColor: formData.create_merchant ? branding.primaryColor : branding.textSecondaryColor,
                                                        boxShadow: formData.create_merchant ? `0 10px 15px -3px ${branding.primaryColor}25` : 'none'
                                                    }}
                                                >
                                                    {formData.create_merchant && (
                                                        <CheckCircle
                                                            className="w-3 h-3"
                                                            style={{ color: branding.textColor }}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex flex-col">
                                                <span
                                                    className="text-sm font-semibold transition-colors"
                                                    style={{ color: branding.textSecondaryColor }}
                                                >
                                                    Create New Merchant
                                                </span>
                                                <span
                                                    className="text-xs"
                                                    style={{ color: branding.textSecondaryColor }}
                                                >
                                                    {formData.create_merchant ? 'Will create a new merchant' : 'Will assign to existing merchant'}
                                                </span>
                                            </div>
                                        </label>
                                    </div>

                                    {formData.create_merchant ? (
                                        <div className="space-y-6">
                                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                                <div>
                                                    <label
                                                        className="block mb-3 text-sm font-semibold"
                                                        style={pageStyles.textSecondary}
                                                    >
                                                        Merchant Name *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={formData.merchant_name || ''}
                                                        onChange={e => handleInputChange('merchant_name', e.target.value)}
                                                        className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 ${errors.merchant_name
                                                            ? 'border-orange-500 focus:ring-orange-500'
                                                            : 'hover:border-gray-500'
                                                            }`}
                                                        style={{
                                                            backgroundColor: `${branding.surfaceColor}80`,
                                                            borderColor: errors.merchant_name
                                                                ? statusColors.error.borderColor
                                                                : `${branding.textSecondaryColor}30`,
                                                            color: branding.textColor,
                                                        }}
                                                        placeholder="Acme Corp"
                                                    />
                                                    {errors.merchant_name && (
                                                        <p
                                                            className="flex items-center mt-2 space-x-1 text-sm"
                                                            style={{ color: statusColors.error.color }}
                                                        >
                                                            <XCircle className="w-4 h-4" />
                                                            <span>{errors.merchant_name}</span>
                                                        </p>
                                                    )}
                                                </div>

                                                <div>
                                                    <label
                                                        className="block mb-3 text-sm font-semibold"
                                                        style={pageStyles.textSecondary}
                                                    >
                                                        Merchant Email *
                                                    </label>
                                                    <input
                                                        type="email"
                                                        value={formData.merchant_email || ''}
                                                        onChange={e => handleInputChange('merchant_email', e.target.value)}
                                                        className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 ${errors.merchant_email
                                                            ? 'border-orange-500 focus:ring-orange-500'
                                                            : 'hover:border-gray-500'
                                                            }`}
                                                        style={{
                                                            backgroundColor: `${branding.surfaceColor}80`,
                                                            borderColor: errors.merchant_email
                                                                ? statusColors.error.borderColor
                                                                : `${branding.textSecondaryColor}30`,
                                                            color: branding.textColor,
                                                        }}
                                                        placeholder="contact@acmecorp.com"
                                                    />
                                                    {errors.merchant_email && (
                                                        <p
                                                            className="flex items-center mt-2 space-x-1 text-sm"
                                                            style={{ color: statusColors.error.color }}
                                                        >
                                                            <XCircle className="w-4 h-4" />
                                                            <span>{errors.merchant_email}</span>
                                                        </p>
                                                    )}
                                                </div>

                                                <div className="md:col-span-2">
                                                    <label
                                                        className="block mb-3 text-sm font-semibold"
                                                        style={pageStyles.textSecondary}
                                                    >
                                                        Merchant Description
                                                    </label>
                                                    <textarea
                                                        value={formData.merchant_description || ''}
                                                        onChange={e => handleInputChange('merchant_description', e.target.value)}
                                                        rows={3}
                                                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                                        style={{
                                                            backgroundColor: `${branding.surfaceColor}80`,
                                                            borderColor: `${branding.textSecondaryColor}30`,
                                                            color: branding.textColor,
                                                        }}
                                                        placeholder="Describe the merchant's business"
                                                    />
                                                </div>

                                                <div>
                                                    <label
                                                        className="block mb-3 text-sm font-semibold"
                                                        style={pageStyles.textSecondary}
                                                    >
                                                        Currency *
                                                    </label>
                                                    <div className="relative" ref={currencyDropdownRef}>
                                                        <div className="relative">
                                                            <Search
                                                                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                                                                style={{ color: branding.textSecondaryColor }}
                                                            />
                                                            <input
                                                                type="text"
                                                                value={currencySearchQuery}
                                                                onChange={e => handleCurrencySearch(e.target.value)}
                                                                onFocus={() => setShowCurrencyDropdown(true)}
                                                                placeholder="Search currencies (e.g., USD, Euro, Dollar)"
                                                                className="w-full px-4 py-3 pl-10 pr-10 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                                                style={{
                                                                    backgroundColor: `${branding.surfaceColor}80`,
                                                                    borderColor: `${branding.textSecondaryColor}30`,
                                                                    color: branding.textColor,
                                                                }}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                                                                className="absolute text-gray-400 transform -translate-y-1/2 right-4 top-1/2 hover:text-gray-600"
                                                            >
                                                                <ChevronDown
                                                                    className={`h-5 w-5 transition-transform ${showCurrencyDropdown ? 'rotate-180' : ''}`}
                                                                />
                                                            </button>
                                                        </div>

                                                        {showCurrencyDropdown && (
                                                            <div
                                                                className="absolute z-50 w-full mt-2 overflow-y-auto border rounded-xl shadow-lg max-h-64"
                                                                style={{
                                                                    backgroundColor: branding.surfaceColor,
                                                                    borderColor: `${branding.textSecondaryColor}30`,
                                                                }}
                                                            >
                                                                {currencyLoading ? (
                                                                    <div className="flex items-center justify-center p-4">
                                                                        <Loader2 className="w-5 h-5 animate-spin" style={{ color: branding.primaryColor }} />
                                                                    </div>
                                                                ) : currencies.length > 0 ? (
                                                                    currencies.map(currency => (
                                                                        <button
                                                                            key={currency.id}
                                                                            type="button"
                                                                            onClick={() => selectCurrency(currency)}
                                                                            className="w-full px-4 py-3 text-left hover:bg-gray-700 transition-colors"
                                                                            style={{
                                                                                color: branding.textColor,
                                                                            }}
                                                                        >
                                                                            {currency.code} - {currency.name}
                                                                        </button>
                                                                    ))
                                                                ) : currencySearchQuery.length > 0 ? (
                                                                    <div className="p-4 text-center" style={{ color: branding.textSecondaryColor }}>
                                                                        <p className="text-sm">
                                                                            No currencies found for "{currencySearchQuery}"
                                                                        </p>
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {errors.merchant_currency_id && (
                                                        <p
                                                            className="flex items-center mt-2 space-x-1 text-sm"
                                                            style={{ color: statusColors.error.color }}
                                                        >
                                                            <XCircle className="w-4 h-4" />
                                                            <span>{errors.merchant_currency_id}</span>
                                                        </p>
                                                    )}
                                                </div>

                                                <div>
                                                    <label
                                                        className="block mb-3 text-sm font-semibold"
                                                        style={pageStyles.textSecondary}
                                                    >
                                                        Commission Rate
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        max="100"
                                                        value={formData.merchant_commission_rate || 0}
                                                        onChange={e => handleInputChange('merchant_commission_rate', parseFloat(e.target.value) || 0)}
                                                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                                        style={{
                                                            backgroundColor: `${branding.surfaceColor}80`,
                                                            borderColor: `${branding.textSecondaryColor}30`,
                                                            color: branding.textColor,
                                                        }}
                                                        placeholder="2.50"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-6">
                                            <div>
                                                <label
                                                    className="block mb-3 text-sm font-semibold"
                                                    style={pageStyles.textSecondary}
                                                >
                                                    Select Existing Merchant
                                                </label>
                                                <div className="relative" ref={merchantDropdownRef}>
                                                    <div className="relative">
                                                        <Search
                                                            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                                                            style={{ color: branding.textSecondaryColor }}
                                                        />
                                                        <input
                                                            type="text"
                                                            value={merchantSearchQuery}
                                                            onChange={e => handleMerchantSearch(e.target.value)}
                                                            onFocus={() => setShowMerchantDropdown(true)}
                                                            placeholder="Search merchants by name or email"
                                                            className="w-full px-4 py-3 pl-10 pr-10 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                                            style={{
                                                                backgroundColor: `${branding.surfaceColor}80`,
                                                                borderColor: `${branding.textSecondaryColor}30`,
                                                                color: branding.textColor,
                                                            }}
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowMerchantDropdown(!showMerchantDropdown)}
                                                            className="absolute text-gray-400 transform -translate-y-1/2 right-4 top-1/2 hover:text-gray-600"
                                                        >
                                                            <ChevronDown
                                                                className={`h-5 w-5 transition-transform ${showMerchantDropdown ? 'rotate-180' : ''}`}
                                                            />
                                                        </button>
                                                    </div>

                                                    {showMerchantDropdown && (
                                                        <div
                                                            className="absolute z-50 w-full mt-2 overflow-y-auto border rounded-xl shadow-lg max-h-64"
                                                            style={{
                                                                backgroundColor: branding.surfaceColor,
                                                                borderColor: `${branding.textSecondaryColor}30`,
                                                            }}
                                                        >
                                                            {merchantLoading ? (
                                                                <div className="flex items-center justify-center p-4">
                                                                    <Loader2 className="w-5 h-5 animate-spin" style={{ color: branding.primaryColor }} />
                                                                </div>
                                                            ) : merchants.length > 0 ? (
                                                                merchants.map(merchant => (
                                                                    <button
                                                                        key={merchant.id}
                                                                        type="button"
                                                                        onClick={() => selectMerchant(merchant)}
                                                                        className="w-full px-4 py-3 text-left hover:bg-gray-700 transition-colors"
                                                                        style={{
                                                                            color: branding.textColor,
                                                                        }}
                                                                    >
                                                                        <div className="font-medium">{merchant.company_name}</div>
                                                                        <div className="text-sm opacity-70">{merchant.email}</div>
                                                                    </button>
                                                                ))
                                                            ) : merchantSearchQuery.length > 0 ? (
                                                                <div className="p-4 text-center" style={{ color: branding.textSecondaryColor }}>
                                                                    <p className="text-sm">No merchants found for "{merchantSearchQuery}"</p>
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    )}
                                                </div>
                                                {errors.merchant_id && (
                                                    <p
                                                        className="flex items-center mt-2 space-x-1 text-sm"
                                                        style={{ color: statusColors.error.color }}
                                                    >
                                                        <XCircle className="w-4 h-4" />
                                                        <span>{errors.merchant_id}</span>
                                                    </p>
                                                )}
                                            </div>

                                            {/* Allow editing merchant details when merchant is selected */}
                                            {formData.merchant_id && (
                                                <div className="space-y-6">
                                                    <div className="pb-4 border-b" style={{ borderBottomColor: `${branding.textSecondaryColor}20` }}>
                                                        <h3
                                                            className="flex items-center space-x-3 text-xl font-semibold"
                                                            style={pageStyles.text}
                                                        >
                                                            <div
                                                                className="p-2 rounded-lg"
                                                                style={{
                                                                    backgroundColor: `${branding.primaryColor}20`
                                                                }}
                                                            >
                                                                <Building
                                                                    className="w-5 h-5"
                                                                    style={{ color: branding.primaryColor }}
                                                                />
                                                            </div>
                                                            <span>Edit Merchant Details</span>
                                                        </h3>
                                                        <p
                                                            className="mt-2"
                                                            style={pageStyles.textSecondary}
                                                        >
                                                            Update merchant information
                                                        </p>
                                                    </div>

                                                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                                        <div>
                                                            <label
                                                                className="block mb-3 text-sm font-semibold"
                                                                style={pageStyles.textSecondary}
                                                            >
                                                                Merchant Name
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={formData.merchant_name || ''}
                                                                onChange={e => handleInputChange('merchant_name', e.target.value)}
                                                                className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                                                style={{
                                                                    backgroundColor: `${branding.surfaceColor}80`,
                                                                    borderColor: `${branding.textSecondaryColor}30`,
                                                                    color: branding.textColor,
                                                                }}
                                                                placeholder="Acme Corp"
                                                            />
                                                        </div>

                                                        <div>
                                                            <label
                                                                className="block mb-3 text-sm font-semibold"
                                                                style={pageStyles.textSecondary}
                                                            >
                                                                Merchant Email
                                                            </label>
                                                            <input
                                                                type="email"
                                                                value={formData.merchant_email || ''}
                                                                onChange={e => handleInputChange('merchant_email', e.target.value)}
                                                                className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                                                style={{
                                                                    backgroundColor: `${branding.surfaceColor}80`,
                                                                    borderColor: `${branding.textSecondaryColor}30`,
                                                                    color: branding.textColor,
                                                                }}
                                                                placeholder="contact@acmecorp.com"
                                                            />
                                                        </div>

                                                        <div className="md:col-span-2">
                                                            <label
                                                                className="block mb-3 text-sm font-semibold"
                                                                style={pageStyles.textSecondary}
                                                            >
                                                                Merchant Description
                                                            </label>
                                                            <textarea
                                                                value={formData.merchant_description || ''}
                                                                onChange={e => handleInputChange('merchant_description', e.target.value)}
                                                                rows={3}
                                                                className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                                                style={{
                                                                    backgroundColor: `${branding.surfaceColor}80`,
                                                                    borderColor: `${branding.textSecondaryColor}30`,
                                                                    color: branding.textColor,
                                                                }}
                                                                placeholder="Describe the merchant's business"
                                                            />
                                                        </div>

                                                        <div>
                                                            <label
                                                                className="block mb-3 text-sm font-semibold"
                                                                style={pageStyles.textSecondary}
                                                            >
                                                                Currency
                                                            </label>
                                                            <div className="relative" ref={currencyDropdownRef}>
                                                                <div className="relative">
                                                                    <Search
                                                                        className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                                                                        style={{ color: branding.textSecondaryColor }}
                                                                    />
                                                                    <input
                                                                        type="text"
                                                                        value={currencySearchQuery}
                                                                        onChange={e => handleCurrencySearch(e.target.value)}
                                                                        onFocus={() => setShowCurrencyDropdown(true)}
                                                                        placeholder="Search currencies (e.g., USD, Euro, Dollar)"
                                                                        className="w-full px-4 py-3 pl-10 pr-10 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                                                        style={{
                                                                            backgroundColor: `${branding.surfaceColor}80`,
                                                                            borderColor: `${branding.textSecondaryColor}30`,
                                                                            color: branding.textColor,
                                                                        }}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                                                                        className="absolute text-gray-400 transform -translate-y-1/2 right-4 top-1/2 hover:text-gray-600"
                                                                    >
                                                                        <ChevronDown
                                                                            className={`h-5 w-5 transition-transform ${showCurrencyDropdown ? 'rotate-180' : ''}`}
                                                                        />
                                                                    </button>
                                                                </div>

                                                                {showCurrencyDropdown && (
                                                                    <div
                                                                        className="absolute z-50 w-full mt-2 overflow-y-auto border rounded-xl shadow-lg max-h-64"
                                                                        style={{
                                                                            backgroundColor: branding.surfaceColor,
                                                                            borderColor: `${branding.textSecondaryColor}30`,
                                                                        }}
                                                                    >
                                                                        {currencyLoading ? (
                                                                            <div className="flex items-center justify-center p-4">
                                                                                <Loader2 className="w-5 h-5 animate-spin" style={{ color: branding.primaryColor }} />
                                                                            </div>
                                                                        ) : currencies.length > 0 ? (
                                                                            currencies.map(currency => (
                                                                                <button
                                                                                    key={currency.id}
                                                                                    type="button"
                                                                                    onClick={() => selectCurrency(currency)}
                                                                                    className="w-full px-4 py-3 text-left hover:bg-gray-700 transition-colors"
                                                                                    style={{
                                                                                        color: branding.textColor,
                                                                                    }}
                                                                                >
                                                                                    {currency.code} - {currency.name}
                                                                                </button>
                                                                            ))
                                                                        ) : currencySearchQuery.length > 0 ? (
                                                                            <div className="p-4 text-center" style={{ color: branding.textSecondaryColor }}>
                                                                                <p className="text-sm">
                                                                                    No currencies found for "{currencySearchQuery}"
                                                                                </p>
                                                                            </div>
                                                                        ) : null}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <label
                                                                className="block mb-3 text-sm font-semibold"
                                                                style={pageStyles.textSecondary}
                                                            >
                                                                Commission Rate
                                                            </label>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                max="100"
                                                                value={formData.merchant_commission_rate || 0}
                                                                onChange={e => handleInputChange('merchant_commission_rate', parseFloat(e.target.value) || 0)}
                                                                className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                                                style={{
                                                                    backgroundColor: `${branding.surfaceColor}80`,
                                                                    borderColor: `${branding.textSecondaryColor}30`,
                                                                    color: branding.textColor,
                                                                }}
                                                                placeholder="2.50"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Crypto Payout Configuration - Available for both new and existing merchants */}
                                    {(formData.merchant_id || formData.create_merchant) ? (
                                        <div className="space-y-6">
                                            <div className="pb-4 border-b" style={{ borderBottomColor: `${branding.textSecondaryColor}20` }}>
                                                <h3
                                                    className="flex items-center space-x-3 text-xl font-semibold"
                                                    style={{
                                                        color: formData.merchant_payout_crypto_enabled ? branding.textColor : branding.textSecondaryColor
                                                    }}
                                                >
                                                    <div
                                                        className="p-2 rounded-lg"
                                                        style={{
                                                            backgroundColor: formData.merchant_payout_crypto_enabled ? `${branding.primaryColor}20` : `${branding.surfaceColor}80`
                                                        }}
                                                    >
                                                        <Building
                                                            className="w-5 h-5"
                                                            style={{
                                                                color: formData.merchant_payout_crypto_enabled ? branding.primaryColor : branding.textSecondaryColor
                                                            }}
                                                        />
                                                    </div>
                                                    <span>Crypto Payout Configuration</span>
                                                </h3>
                                                <p
                                                    className="mt-2"
                                                    style={{
                                                        color: formData.merchant_payout_crypto_enabled ? branding.textColor : branding.textSecondaryColor
                                                    }}
                                                >
                                                    Configure cryptocurrency payout settings for this merchant
                                                </p>
                                            </div>

                                            <div className="flex items-center space-x-4">
                                                <label className="flex items-center space-x-3 cursor-pointer group">
                                                    <div className="relative">
                                                        <input
                                                            type="checkbox"
                                                            checked={formData.merchant_payout_crypto_enabled || false}
                                                            onChange={e => handleInputChange('merchant_payout_crypto_enabled', e.target.checked)}
                                                            className="sr-only"
                                                        />
                                                        <div
                                                            className={`w-5 h-5 border-2 rounded-md transition-all duration-200 flex items-center justify-center ${formData.merchant_payout_crypto_enabled
                                                                ? 'shadow-lg'
                                                                : 'group-hover:border-orange-400 group-hover:bg-gray-600/50'
                                                                }`}
                                                            style={{
                                                                backgroundColor: formData.merchant_payout_crypto_enabled ? branding.primaryColor : `${branding.surfaceColor}80`,
                                                                borderColor: formData.merchant_payout_crypto_enabled ? branding.primaryColor : branding.textSecondaryColor,
                                                                boxShadow: formData.merchant_payout_crypto_enabled ? `0 10px 15px -3px ${branding.primaryColor}25` : 'none'
                                                            }}
                                                        >
                                                            {formData.merchant_payout_crypto_enabled && (
                                                                <CheckCircle
                                                                    className="w-3 h-3"
                                                                    style={{ color: branding.textColor }}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span
                                                            className="text-sm font-semibold transition-colors"
                                                            style={{
                                                                color: formData.merchant_payout_crypto_enabled ? branding.textColor : branding.textSecondaryColor
                                                            }}
                                                        >
                                                            Enable Crypto Payouts
                                                        </span>
                                                        <span
                                                            className="text-xs"
                                                            style={{
                                                                color: formData.merchant_payout_crypto_enabled ? branding.textColor : branding.textSecondaryColor
                                                            }}
                                                        >
                                                            {formData.merchant_payout_crypto_enabled ? 'Crypto payouts enabled' : 'Crypto payouts disabled'}
                                                        </span>
                                                    </div>
                                                </label>
                                            </div>

                                            {formData.merchant_payout_crypto_enabled && (
                                                <div className="space-y-6">
                                                    <div>
                                                        <label
                                                            className="block mb-3 text-sm font-semibold"
                                                            style={pageStyles.textSecondary}
                                                        >
                                                            Crypto Type *
                                                        </label>
                                                        <div className="relative" ref={cryptoTypeDropdownRef}>
                                                            <div className="relative">
                                                                <Search
                                                                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                                                                    style={{ color: branding.textSecondaryColor }}
                                                                />
                                                                <input
                                                                    type="text"
                                                                    value={cryptoTypeSearchQuery}
                                                                    onChange={e => handleCryptoTypeSearch(e.target.value)}
                                                                    onFocus={() => setShowCryptoTypeDropdown(true)}
                                                                    placeholder="Search crypto types (e.g., BTC, ETH, USDT)"
                                                                    className="w-full px-4 py-3 pl-10 pr-10 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 hover:border-gray-500"
                                                                    style={{
                                                                        backgroundColor: `${branding.surfaceColor}80`,
                                                                        borderColor: errors.merchant_payout_crypto_type_id
                                                                            ? statusColors.error.borderColor
                                                                            : `${branding.textSecondaryColor}30`,
                                                                        color: branding.textColor,
                                                                    }}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setShowCryptoTypeDropdown(!showCryptoTypeDropdown)}
                                                                    className="absolute text-gray-400 transform -translate-y-1/2 right-4 top-1/2 hover:text-gray-600"
                                                                >
                                                                    <ChevronDown
                                                                        className={`h-5 w-5 transition-transform ${showCryptoTypeDropdown ? 'rotate-180' : ''}`}
                                                                    />
                                                                </button>
                                                            </div>

                                                            {showCryptoTypeDropdown && (
                                                                <div
                                                                    className="absolute z-50 w-full mt-2 overflow-y-auto border rounded-xl shadow-lg max-h-64"
                                                                    style={{
                                                                        backgroundColor: branding.surfaceColor,
                                                                        borderColor: `${branding.textSecondaryColor}30`,
                                                                    }}
                                                                >
                                                                    {cryptoTypeLoading ? (
                                                                        <div className="flex items-center justify-center p-4">
                                                                            <Loader2 className="w-5 h-5 animate-spin" style={{ color: branding.primaryColor }} />
                                                                        </div>
                                                                    ) : cryptoTypes.length > 0 ? (
                                                                        cryptoTypes.map(cryptoType => (
                                                                            <button
                                                                                key={cryptoType.id}
                                                                                type="button"
                                                                                onClick={() => selectCryptoType(cryptoType)}
                                                                                className="w-full px-4 py-3 text-left hover:bg-gray-700 transition-colors"
                                                                                style={{
                                                                                    color: branding.textColor,
                                                                                }}
                                                                            >
                                                                                <div className="font-medium">{cryptoType.code} - {cryptoType.name}</div>
                                                                                <div className="text-sm opacity-70">Min payout: {cryptoType.min_payout}</div>
                                                                            </button>
                                                                        ))
                                                                    ) : cryptoTypeSearchQuery.length > 0 ? (
                                                                        <div className="p-4 text-center" style={{ color: branding.textSecondaryColor }}>
                                                                            <p className="text-sm">
                                                                                No crypto types found for "{cryptoTypeSearchQuery}"
                                                                            </p>
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {errors.merchant_payout_crypto_type_id && (
                                                            <p
                                                                className="flex items-center mt-2 space-x-1 text-sm"
                                                                style={{ color: statusColors.error.color }}
                                                            >
                                                                <XCircle className="w-4 h-4" />
                                                                <span>{errors.merchant_payout_crypto_type_id}</span>
                                                            </p>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <label
                                                            className="block mb-3 text-sm font-semibold"
                                                            style={pageStyles.textSecondary}
                                                        >
                                                            Wallet Address *
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={formData.merchant_payout_wallet_address || ''}
                                                            onChange={e => handleInputChange('merchant_payout_wallet_address', e.target.value)}
                                                            className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 ${errors.merchant_payout_wallet_address
                                                                ? 'border-orange-500 focus:ring-orange-500'
                                                                : 'hover:border-gray-500'
                                                                }`}
                                                            style={{
                                                                backgroundColor: `${branding.surfaceColor}80`,
                                                                borderColor: errors.merchant_payout_wallet_address
                                                                    ? statusColors.error.borderColor
                                                                    : `${branding.textSecondaryColor}30`,
                                                                color: branding.textColor,
                                                            }}
                                                            placeholder="Enter cryptocurrency wallet address"
                                                        />
                                                        {errors.merchant_payout_wallet_address && (
                                                            <p
                                                                className="flex items-center mt-2 space-x-1 text-sm"
                                                                style={{ color: statusColors.error.color }}
                                                            >
                                                                <XCircle className="w-4 h-4" />
                                                                <span>{errors.merchant_payout_wallet_address}</span>
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : null}
                                </div>
                            )}

                            {/* Email Settings */}
                            <div className="space-y-6">
                                <div className="pb-4 border-b" style={{ borderBottomColor: `${branding.textSecondaryColor}20` }}>
                                    <h2
                                        className="flex items-center space-x-3 text-2xl font-semibold"
                                        style={pageStyles.text}
                                    >
                                        <div
                                            className="p-2 rounded-lg"
                                            style={{
                                                backgroundColor: `${branding.primaryColor}20`
                                            }}
                                        >
                                            <Mail
                                                className="w-6 h-6"
                                                style={{ color: branding.primaryColor }}
                                            />
                                        </div>
                                        <span>Email Settings</span>
                                    </h2>
                                    <p
                                        className="mt-2"
                                        style={pageStyles.textSecondary}
                                    >
                                        Configure email notifications
                                    </p>
                                </div>

                                <div className="flex items-center space-x-4">
                                    <label className="flex items-center space-x-3 cursor-pointer group">
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                checked={formData.send_welcome_email || false}
                                                onChange={e => handleInputChange('send_welcome_email', e.target.checked)}
                                                className="sr-only"
                                            />
                                            <div
                                                className={`w-5 h-5 border-2 rounded-md transition-all duration-200 flex items-center justify-center ${formData.send_welcome_email
                                                    ? 'shadow-lg'
                                                    : 'group-hover:border-orange-400 group-hover:bg-gray-600/50'
                                                    }`}
                                                style={{
                                                    backgroundColor: formData.send_welcome_email ? branding.primaryColor : `${branding.surfaceColor}80`,
                                                    borderColor: formData.send_welcome_email ? branding.primaryColor : branding.textSecondaryColor,
                                                    boxShadow: formData.send_welcome_email ? `0 10px 15px -3px ${branding.primaryColor}25` : 'none'
                                                }}
                                            >
                                                {formData.send_welcome_email && (
                                                    <CheckCircle
                                                        className="w-3 h-3"
                                                        style={{ color: branding.textColor }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex flex-col">
                                            <span
                                                className="text-sm font-semibold transition-colors"
                                                style={{
                                                    color: formData.send_welcome_email ? branding.textColor : branding.textSecondaryColor
                                                }}
                                            >
                                                Send Welcome Email
                                            </span>
                                            <span
                                                className="text-xs"
                                                style={{ color: branding.textSecondaryColor }}
                                            >
                                                Will send welcome email with temporary password
                                            </span>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* Form Actions */}
                            <div className="flex items-center justify-end space-x-4 pt-6 border-t" style={{ borderColor: `${branding.textSecondaryColor}20` }}>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => navigate(`/admin/users/${id}`)}
                                    className="px-6 py-3"
                                    style={{
                                        color: branding.textColor,
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="px-6 py-3 text-white"
                                    style={{
                                        backgroundColor: branding.primaryColor,
                                    }}
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle className="w-5 h-5 mr-2" />
                                            Update User
                                        </>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}

